/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { EvidencePart, IEvidence, ProgramData, WPBAForm } from "@interfaces/Interfaces";
import { IonItem, IonLabel, IonList, IonSelect, IonSelectOption } from "@ionic/react";
import { Text, View } from "@react-pdf/renderer";
import { Capacitor } from "@capacitor/core";
import { updatePopoverTriggerWidth } from "@utils/popoverUtils";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom } from "@state/State";
import { format, isValid } from "date-fns";
import { useHistory, useLocation } from "react-router";

type SelectOption = {
  key: string;
  label: string;
};

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any) => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  initialValue?: any;
  program?: ProgramData;
};

const deferHeavyWorkAfterModalMs = (): number => {
  switch (Capacitor.getPlatform()) {
    case "ios":
      return 550;
    case "android":
      return 320;
    default:
      return 0;
  }
};

const formatEvidenceRowDate = (dateValue: unknown): string => {
  const d = new Date(dateValue as string | number | Date);
  if (!isValid(d)) return "—";
  return format(d, "dd / MM / yyyy");
};

const getEvidenceForWPBAForm = (evidence: IEvidence[] | null | undefined, form: WPBAForm): SelectOption[] => {
  const list = Array.isArray(evidence) ? evidence : [];
  const definitionIds = new Set((form["Evidence Definitions"] ?? []).map((def) => def.ID).filter(Boolean));

  return list
    .filter((item) => {
      const evidenceJSON = item.evidenceJSON;
      if (!evidenceJSON || item.draft || (evidenceJSON?.OnHoldReset ?? 0) === 1) return false;
      if (evidenceJSON?.programInfo?.supervisorCopy) return false;
      return definitionIds.has(evidenceJSON?.programInfo?.programEvidenceDefinitionID);
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map((item) => {
      const title = item.evidenceJSON?.Title ?? "";
      return { key: String(item.id), label: `${title} - ${formatEvidenceRowDate(item.date)}` };
    });
};

const PDFDisplay: React.FC<Props> = (props) => {
  const displayLabel = props.data?.label ?? props.data ?? "";
  return (
    <View>
      <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
      <Text style={props.pdfStyles.evidenceDate}>{displayLabel}</Text>
    </View>
  );
};

const DisplayMode: React.FC<Props> = (props) => {
  const history = useHistory();
  const location = useLocation();
  const allEvidenceLB = useRecoilValueLoadable(evidenceAtom);
  const displayLabel = props.data?.label ?? props.data ?? "";
  const evidenceId = props.data?.id;

  const evidenceExists =
    evidenceId &&
    allEvidenceLB.state === "hasValue" &&
    (allEvidenceLB.contents ?? []).some((e: IEvidence) => String(e.id) === String(evidenceId));

  const handleClick = (): void => {
    let route: string;
    if (location.pathname.startsWith("/dashboard/program")) {
      const programId = props.program?.ID ?? location.pathname.split("/").filter(Boolean)[2];
      route = `/dashboard/program/${programId}/read/${evidenceId}`;
    } else {
      route = `/evidence/read/${evidenceId}`;
    }
    history.push(route);
  };

  return (
    <div className="readEvidenceBlock">
      <div className="readEvidenceHeader">{props.definition["Display Title"] || props.definition.Name}</div>
      {evidenceExists ? (
        <button
          className="acc-underline cursor-pointer text-left text-cta-blue underline"
          onClick={handleClick}
        >
          {displayLabel}
        </button>
      ) : (
        <div className="readEvidenceText">{displayLabel}</div>
      )}
      <div className="mt-4 h-[1px] w-full bg-grey-30" />
    </div>
  );
};

const IonSelectWrapper: React.FC<{
  value: string;
  placeholder: string;
  label: string;
  options: SelectOption[];
  useActionSheet: boolean;
  onChange: (value: string) => void;
}> = ({ value, placeholder, label, options, useActionSheet, onChange }) => (
  <IonList
    mode="md"
    className="reflectionActivityList w-full"
    color="white"
  >
    <IonItem
      mode="md"
      className="reflectionActivityItem"
      color="white"
    >
      <IonSelect
        color="white"
        mode="md"
        className="reflectionActivitySelectItem w-full"
        interface={useActionSheet ? "action-sheet" : "popover"}
        justify="start"
        {...(!useActionSheet
          ? {
              interfaceOptions: {
                size: "cover",
                alignment: "start",
                cssClass: "evidence-select-popover",
              },
            }
          : {})}
        placeholder={placeholder}
        value={value || undefined}
        onIonChange={(ev) => onChange(ev.detail.value)}
        onClick={useActionSheet ? undefined : updatePopoverTriggerWidth}
        aria-label={label}
      >
        {options.map((item) => (
          <IonSelectOption
            color="white"
            key={item.key}
            value={item.key}
          >
            <IonLabel aria-label={item.label}>{item.label}</IonLabel>
          </IonSelectOption>
        ))}
      </IonSelect>
    </IonItem>
  </IonList>
);

const NativeSelectWrapper: React.FC<{
  value: string;
  placeholder: string;
  label: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}> = ({ value, placeholder, label, options, onChange }) => (
  <select
    className="custom-select-arrow-large evidenceSelectText w-full cursor-pointer appearance-none truncate bg-white py-2 pl-0 pr-10 font-acc-600 leading-tight text-grey-80"
    style={{ textDecoration: "var(--accessibility-underline-links)" }}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    aria-label={label}
  >
    <option
      value=""
      disabled
    >
      {placeholder}
    </option>
    {options.map((item) => (
      <option
        key={item.key}
        value={item.key}
      >
        {item.label}
      </option>
    ))}
  </select>
);

const Interactive: React.FC<Props> = (props) => {
  const allEvidenceLB = useRecoilValueLoadable(evidenceAtom);
  const deferOptionsBuild = Capacitor.getPlatform() !== "web";
  const useNativeHtmlSelect = deferOptionsBuild;
  const useActionSheet = Capacitor.getPlatform() === "ios";

  const allEvidence = allEvidenceLB.state === "hasValue" ? allEvidenceLB.contents ?? [] : [];

  const wpbaForms: WPBAForm[] = useMemo(
    () => [...(props.program?.["WPBA Forms"] ?? [])].sort((a, b) => a.Name.localeCompare(b.Name)),
    [props.program]
  );

  const formOptions: SelectOption[] = useMemo(
    () =>
      wpbaForms
        .filter((form) => getEvidenceForWPBAForm(allEvidence, form).length > 0)
        .map((form) => ({ key: form.ID, label: form.Name })),
    [wpbaForms, allEvidence]
  );

  const [selectedFormId, setSelectedFormId] = useState<string>("");
  const [selectedEvidence, setSelectedEvidence] = useState<{ id: string; label: string } | null>(
    props.initialValue ?? null
  );

  const [nativeEvidenceOptions, setNativeEvidenceOptions] = useState<SelectOption[]>([]);
  const [nativeOptionsReady, setNativeOptionsReady] = useState(!deferOptionsBuild);

  const selectedForm = useMemo(
    () => wpbaForms.find((f) => f.ID === selectedFormId) ?? null,
    [wpbaForms, selectedFormId]
  );

  const memoEvidenceOptions = useMemo((): SelectOption[] => {
    if (deferOptionsBuild || !selectedForm) return [];
    return getEvidenceForWPBAForm(allEvidence, selectedForm);
  }, [deferOptionsBuild, allEvidence, selectedForm]);

  useEffect(() => {
    if (!deferOptionsBuild) return;
    if (!selectedForm) {
      setNativeEvidenceOptions([]);
      setNativeOptionsReady(true);
      return;
    }

    let cancelled = false;
    setNativeOptionsReady(false);

    const run = (): void => {
      if (cancelled) return;
      try {
        const ev = allEvidenceLB.state === "hasValue" ? allEvidenceLB.contents ?? [] : [];
        setNativeEvidenceOptions(getEvidenceForWPBAForm(ev, selectedForm));
      } catch {
        setNativeEvidenceOptions([]);
      }
      if (!cancelled) setNativeOptionsReady(true);
    };

    const timeoutId = window.setTimeout(run, deferHeavyWorkAfterModalMs());
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [deferOptionsBuild, allEvidenceLB.state, allEvidenceLB.contents, selectedForm]);

  const evidenceOptions = deferOptionsBuild ? nativeEvidenceOptions : memoEvidenceOptions;

  const onFormChange = (id: string): void => {
    setSelectedFormId(id);
    setSelectedEvidence(null);
    props.onValueChanged && props.onValueChanged(null);
  };

  const onEvidenceChange = (key: string): void => {
    const found = evidenceOptions.find((o) => o.key === key);
    if (!found) return;
    const value = { id: found.key, label: found.label };
    setSelectedEvidence(value);
    props.onValueChanged && props.onValueChanged(value);
  };

  if (formOptions.length === 0) {
    return (
      <div className="reflectionActivityTypeContainer">
        <div className="reflectionTagsInput text-grey-60">{"No WPBA form evidence available"}</div>
      </div>
    );
  }

  const evidenceLoadingOrEmpty = deferOptionsBuild && selectedFormId && !nativeOptionsReady;

  return (
    <div className="flex flex-col gap-3">
      {/* WPBA form type select */}
      <div className="reflectionActivityTypeContainer">
        {useNativeHtmlSelect ? (
          <NativeSelectWrapper
            value={selectedFormId}
            placeholder="Select form type"
            label="Select form type"
            options={formOptions}
            onChange={onFormChange}
          />
        ) : (
          <IonSelectWrapper
            value={selectedFormId}
            placeholder="Select form type"
            label="Select form type"
            options={formOptions}
            useActionSheet={useActionSheet}
            onChange={onFormChange}
          />
        )}
      </div>

      {/* Evidence select — only shown once a form type is chosen */}
      {selectedFormId && (
        <div className="reflectionActivityTypeContainer">
          {evidenceLoadingOrEmpty ? (
            <div
              className="min-h-[48px] rounded-md border border-grey-40 bg-white"
              aria-busy="true"
            />
          ) : evidenceOptions.length === 0 ? (
            <div className="reflectionTagsInput text-grey-60">{"No evidence for this form type"}</div>
          ) : useNativeHtmlSelect ? (
            <NativeSelectWrapper
              value={selectedEvidence?.id ?? ""}
              placeholder="Select evidence"
              label="Select evidence"
              options={evidenceOptions}
              onChange={onEvidenceChange}
            />
          ) : (
            <IonSelectWrapper
              value={selectedEvidence?.id ?? ""}
              placeholder="Select evidence"
              label="Select evidence"
              options={evidenceOptions}
              useActionSheet={useActionSheet}
              onChange={onEvidenceChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

const WPBAEvidenceSelect: React.FC<Props> = (props) => {
  if (props.isPDF) {
    return <PDFDisplay {...props} />;
  }
  if (props.isDisplay) {
    return <DisplayMode {...props} />;
  }
  return <Interactive {...props} />;
};

export default WPBAEvidenceSelect;
